import Vue from 'vue';
import HttpStatus from '../../utils/httpStatusCodes';
import DueStepRouter from '../../router/dueStepRouter';
import {
    fetchCollectiveAgreementReasonsById,
    fetchCollectiveAgreementsByCodeNaf,
    fetchDueById,
    fetchDueLegalInformation,
    updateDueById,
    createDue
} from '../../service/api/dues.api';


const state = {
    dueState: JSON.parse(window.localStorage.getItem('dueState')) || { },
    isBeneficiaryCoverage: true,
    editDueId: null,
    editCurrentStep: null,
    due: {},
    dueDraft: {},
    collectiveAgreements: [],
    dueReasons: [],
    legalInformation: null,
    isEdit: false
};

const mutations = {
    'SET_DUE' (state, due) {
        state.due = due;
    },
    'SET_DUE_ID' (state, { enterpriseUuid, dueId }) {
        if (state.isEdit) {
            state.editDueId = dueId;
            return;
        }
        if (Object.prototype.hasOwnProperty.call(state.dueState, enterpriseUuid)) {
            return;
        }

        Vue.set(state.dueState, enterpriseUuid, { id: dueId });
        window.localStorage.setItem('dueState', JSON.stringify(state.dueState));
    },
    'DELETE_DUE_ID' (state, { enterpriseUuid }) {
        if (state.isEdit) {
            state.editDueId = null;
            return;
        }

        Vue.delete(state.dueState, enterpriseUuid);
        window.localStorage.setItem('dueState', JSON.stringify(state.dueState));
    },
    'SET_DUE_REASONS' (state, reasons) {
        state.dueReasons = reasons;
    },
    'SET_CURRENT_STEP' (state, { step, enterpriseUuid }) {
        if (state.isEdit) {
            state.editCurrentStep = step;
            return;
        }
        if (!(enterpriseUuid in state.dueState)) {
            return;
        }

        Vue.set(state.dueState, enterpriseUuid, { ...state.dueState[enterpriseUuid], step });
        window.localStorage.setItem('dueState', JSON.stringify(state.dueState));
    },
    'SET_COLLECTIVE_AGREEMENTS' (state, collectiveAgreements) {
        state.collectiveAgreements = collectiveAgreements;
    },
    'SET_LEGAL_INFORMATION' (state, legalInformation) {
        state.legalInformation = legalInformation;
    },
    'CLEAR_DUE_DATA' (state, enterpriseUuid) {
        if (state.isEdit) {
            state.editDueId = null;
            state.editCurrentStep = null;
            state.isEdit = false;
            return;
        }

        Vue.delete(state.dueState, enterpriseUuid);
        window.localStorage.setItem('dueState', JSON.stringify(state.dueState));

        state.due = {};
    },
    'SET_IS_EDIT' (state, isEdit) {
        state.isEdit = isEdit;
    },
    'SET_IS_BENEFICIARY_COVERAGE' (state, isBeneficiaryCoverage) {
        state.isBeneficiaryCoverage = isBeneficiaryCoverage;
    }
};

const actions = {
    async setActiveDue({ commit }, due) {
        commit('SET_DUE', due);
    },
    async setCurrentStep({ commit, rootState }, step) {
        commit('SET_CURRENT_STEP', { step, enterpriseUuid: rootState.enterprise.enterpriseUuid });
    },
    async fetchCollectiveAgreements({ commit }, codeNaf) {
        const { data } = await fetchCollectiveAgreementsByCodeNaf(codeNaf);
        commit('SET_COLLECTIVE_AGREEMENTS', data);
    },
    async createDue({ commit, getters, rootState, dispatch }, formData) {
        if (getters.isStepBackOrEdit) {
            await dispatch('updateCurrentDue', formData);
            return;
        }

        const { data } = await createDue(formData);
        commit('SET_DUE', data);
        commit('SET_DUE_ID', { enterpriseUuid: rootState.enterprise.enterpriseUuid, dueId: data.id });
    },
    async updateCurrentDue({ commit, getters, rootState }, formData) {
        const { data } = await updateDueById(getters.dueId, formData);
        commit('SET_DUE', data);
        commit('SET_DUE_ID', { enterpriseUuid: rootState.enterprise.enterpriseUuid, dueId: data.id });
    },
    async fetchCurrentDue({ commit, getters, rootState }, editDueId = null) {
        switch (true) {
            case null === getters.dueId && !Number.isInteger(parseInt(editDueId)):
                commit('SET_IS_EDIT', false);
                commit('SET_DUE', {});
                return;
            case null !== getters.dueId && !Number.isInteger(parseInt(editDueId)):
                commit('SET_IS_EDIT', false);
                break
            case Number.isInteger(parseInt(editDueId)):
                commit('SET_IS_EDIT', true);
                commit('SET_CURRENT_STEP', { step: null, enterpriseUuid: rootState.enterprise.enterpriseUuid });
                commit('SET_DUE_ID', { enterpriseUuid: rootState.enterprise.enterpriseUuid, dueId: editDueId });
        }

        try {
            const { data } = await fetchDueById(getters.dueId);
            commit('SET_DUE', data);
            commit('SET_DUE_ID', { enterpriseUuid: rootState.enterprise.enterpriseUuid, dueId: data.id });
        } catch ({ response }) {
            if (response.status === HttpStatus.HTTP_NOT_FOUND) {
                commit('DELETE_DUE_ID', { enterpriseUuid: rootState.enterprise.enterpriseUuid });
            }
        }
    },
    async fetchDueReasons({ state, commit }) {
        const { data } = await fetchCollectiveAgreementReasonsById(state.due.collectiveAgreement.id);
        commit('SET_DUE_REASONS', data);
    },
    async fetchDueLegalInformation({ commit, state }) {
        if (state.legalInformation) {
            return;
        }

        const { data } =  await fetchDueLegalInformation();
        commit('SET_LEGAL_INFORMATION', data);
    },
    async clearDueData({ commit, rootState }) {
        commit('CLEAR_DUE_DATA', rootState.enterprise.enterpriseUuid);
    },
    async redirectToCurrentStep({ getters }) {
        if (!getters.isStepBackOrEdit || !getters.dueId) {
            return;
        }

        await DueStepRouter.route(getters.currentStep);
    },
    async setIsBeneficiaryCoverage({ commit }, isBeneficiaryCoverage) {
        commit('SET_IS_BENEFICIARY_COVERAGE', isBeneficiaryCoverage);
    },
};

const getters = {
    due: (state) => state.due,
    collectiveAgreements: (state) => state.collectiveAgreements,
    dueReasons: (state) => state.dueReasons,
    legalInformation: (state) => state.legalInformation,
    isEdit: (state) => state.isEdit,
    updateDueId: (state) => state.editDueId,
    showBeneficiaryCoverageMessage: (state) => !state.isBeneficiaryCoverage,
    dueId: (state, rootState) => {
        if (state.isEdit) {
            return state.editDueId;
        }
        if (Object.prototype.hasOwnProperty.call(state.dueState, rootState.enterpriseUuid)) {
            return state.dueState[rootState.enterpriseUuid].id;
        }

        return null;
    },
    isStepBackOrEdit: (state, rootState) => {
        return Object.prototype.hasOwnProperty.call(state.dueState, rootState.enterpriseUuid)
            || state.isEdit;
    },
    currentStep: (state, rootState) => {
        if (state.isEdit) {
            return state.editCurrentStep;
        }
        if (Object.prototype.hasOwnProperty.call(state.dueState, rootState.enterpriseUuid)) {
            return state.dueState[rootState.enterpriseUuid].step;
        }

        return null;
    },
};

export default {
    state,
    mutations,
    actions,
    getters
};